import { useContext } from 'react';

import { fillName, fillPhone, startedTyping } from '@r1-frontend/bp42/actions';
import submit from '@r1-frontend/bp42/actions/thunk/submit';
import { BP42Ctx } from '@r1-frontend/bp42/BP42Context';
import { useCtxDispatch, useCtxSelector, useIncomingCallbacks } from '@r1-frontend/bp42/hooks';
import selectDataForCallbackForm from '@r1-frontend/bp42/selectors/selectDataForCallbackForm';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import { TField } from '@r1-frontend/ui-react/organisms/Forms/NamePhoneForm';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { POLICY_PAGE_LINK } from '@r1-frontend/shared/constants/routes';
import { useOnceExecute } from '@r1-frontend/shared/hooks/useOnceExecute';

import SpecialOffer from './SpecialOffer';

import * as ST from './styled';

/**
 * Форма заказа обратного звонка
 */
const CallbackForm = ({ className }: { className?: string }): JSX.Element => {
    const { analytics, csrf } = useContext(BP42Ctx);
    const dispatch = useCtxDispatch();
    const callbacks = useIncomingCallbacks();

    const {
        packageName,
        viewDetails,
        needToShowSpecOffer,
        initialValue,
        isStartedTyping,
        isFormValid,
        isLoading,
    } = useCtxSelector(selectDataForCallbackForm);

    const { title, description, specOfferButtonText } = viewDetails.popup;

    const sendStartedFillingName = useOnceExecute(() => {
        analytics.startedFillingName(packageName);
        dispatch(startedTyping());
    });

    const handleChangeName = (data: TField) => {
        // the implementation of the "nameDecorator" of the "SimpleInput" component performs a callback even at the first rendering... it's sad
        if (data.value) {
            sendStartedFillingName();
            dispatch(fillName(data));
        }
    };

    const handleChangePhone = (data: TField) => {
        dispatch(fillPhone(data));
    };

    const handleSpecOfferClick = () => {
        analytics.clickedSpecialOffer(packageName);
    };

    const handleSubmit = () => {
        dispatch(submit(csrf, callbacks, analytics));
    };

    return <ST.LazyCallBack data-test='lazy-callback' className={className}>
        <ST.ContentWrapper>
            {(title || description) && (
                <TextWrapper>
                    {title && (
                        <ST.SH3 data-test='lazy-callback-title'>{title}</ST.SH3>
                    )}
                    {description && (
                        <Paragraph4 $color={COLORS.TextInfo} data-test='lazy-callback-subtitle'>{description}</Paragraph4>
                    )}
                </TextWrapper>
            )}

            <ST.Form data-test='lazy-callback-form'>
                <ST.InputFields>
                    <ST.NameInput
                        name='name'
                        placeholder='Ваше имя'
                        initialValue={initialValue.name}
                        errorMessage='Ожидаются только буквы и дефисы'
                        onChange={handleChangeName}
                        className={'ym-disable-keys'}
                        data-test='lazy-callback-form-name-input'
                    />
                    <ST.PhoneInput
                        name='phone'
                        placeholder='Телефон'
                        initialValue={initialValue.phone}
                        errorMessage='Телефон в формате +7 (999) 999-99-99'
                        onChange={handleChangePhone}
                        className={'ym-disable-keys'}
                        data-test='lazy-callback-form-phone-input'
                    />
                </ST.InputFields>

                <PolicyNote
                    data-test='lazy-callback-form-policy'
                    buttonText={viewDetails.form.buttonText}
                    agreeUrl={POLICY_PAGE_LINK}
                />

                <Button
                    size={'large'}
                    bType={viewDetails.form.buttonType}
                    disabled={isLoading || !isFormValid}
                    onClick={handleSubmit}
                    data-test='lazy-callback-form-submit-button'
                >
                    {viewDetails.form.buttonText}
                </Button>
            </ST.Form>
        </ST.ContentWrapper>

        {isStartedTyping && needToShowSpecOffer && (
            <SpecialOffer
                btnText={specOfferButtonText}
                onClickContinue={handleSpecOfferClick}
                data-test='lazy-callback-special-offer'
            />
        )}
    </ST.LazyCallBack>;
};

export default CallbackForm;
